import { Box, Checkbox, HStack, Text, VStack } from "@chakra-ui/react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import type { EnrichedCustomConversion } from "./types";

dayjs.extend(relativeTime);

export type ConversionCardProps = {
  c: EnrichedCustomConversion;
  selectedIds: Set<string>;
  onToggle: (id: string, selectable: boolean) => void;
  selectable: boolean;
  badge: string;
  badgeColor: string;
  dateLabel?: "Ended" | "Ends";
};

export default function ConversionCard({
  c,
  selectedIds,
  onToggle,
  selectable,
  badge,
  badgeColor,
  dateLabel = "Ended",
}: ConversionCardProps) {
  const endAt = c.event_summary?.end_at;
  const isPast = dateLabel === "Ended" && endAt;
  const timeText = isPast
    ? `Event ended ${dayjs(endAt).fromNow()}`
    : endAt
      ? `${dateLabel} ${dayjs(endAt).format("M/D/YYYY")}`
      : null;

  return (
    <Box
      p={3}
      borderWidth={1}
      borderRadius="md"
      borderColor="gray.200"
      bg={c.deletable ? "white" : "gray.50"}
      cursor={selectable ? "pointer" : "default"}
      onClick={() => selectable && onToggle(c.id, selectable)}
      role={selectable ? "button" : undefined}
      _hover={selectable ? { bg: "gray.50" } : undefined}
    >
      <HStack justify="space-between" align="flex-start">
        <HStack align="center" spacing={3}>
          <Checkbox
            isChecked={selectedIds.has(c.id)}
            isDisabled={!selectable}
            pointerEvents="none"
            aria-hidden
          />
          <VStack align="start" spacing={0}>
            <Text fontWeight="medium" fontSize="sm" lineHeight="tight">
              {c.name}
            </Text>
            {c.event_summary && (c.event_summary.name || timeText) && (
              <Text fontSize="xs" color="gray.500" lineHeight="tight">
                {c.event_summary.name && <>{c.event_summary.name}</>}
                {c.event_summary.name && timeText && " · "}
                {timeText && (
                  <Text as="span" fontWeight={isPast ? "bold" : undefined}>
                    {timeText}
                  </Text>
                )}
              </Text>
            )}
          </VStack>
        </HStack>
        <Text
          fontSize="xs"
          color={badgeColor}
          fontWeight="medium"
          as="span"
          flexShrink={0}
        >
          {badge}
        </Text>
      </HStack>
    </Box>
  );
}
